/* eslint-disable no-tabs */
import React from 'react';

import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css, Global } from '@emotion/core';
import DatePicker from 'react-datepicker';
import IcomoonReact from 'icomoon-react';
import moment from 'moment';

import iconSet from '../../images/teambuildr-selection.json';
import { isKeyboardNav, subscribeKeyboardNav } from '../../utils/keyboardNavMode';
import useIsTouchDevice from '../../hooks/useIsTouchDevice';
import { FOCUS_BLUE, FOCUS_FILL, FOCUS_FILL_DARK } from '../../theme/focusStyles';
import {
  BoxShadow,
} from '../../GlobalStyles';

/* Safari doesn't focus a <button> on click but does blur the input, which trips
   the wrapper's handleFocusOut and closes the calendar before onClick fires.
   Preventing the mousedown default keeps focus in place so the calendar stays
   open. Module-scope = one stable reference (no inline function per render). */
const preventMouseDownDefault = (e) => e.preventDefault();

const DateSelectorContainer = styled('div')`
	margin-top: ${(props) => (props.additionalStyles.margin ? props.additionalStyles.margin : '30px')};
  width: 100%;
  max-width: ${(props) => (props.additionalStyles.maxWidth ? props.additionalStyles.maxWidth : '100%')};
  min-width: ${(props) => (props.additionalStyles.minWidth ? props.additionalStyles.minWidth : '170px')};
	display: flex;
	justify-content: ${(props) => (props.additionalStyles.justifyContent ? props.additionalStyles.justifyContent : 'center')};
	height: 48px;
	align-items: center;
	border-radius: 6px;
	border: ${(props) => (props.showDropShadow ? 'none' : `1px solid ${props.theme.colors.borderMedium}`)};
	font-family: 'Nunito Sans';
	.react-datepicker__header {
		background: ${({ theme }) => theme.colors.surfaceElevated};
		border-bottom: none;
	}
	.react-datepicker-wrapper {
		width: 100%;
	}
	.date-picker {
		border: none;
		height: 40px;
		font-weight: 400;
    width: 100%;
    cursor: pointer;
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
  }
	.react-datepicker__month-container {
		padding: 15px;
		font-family: 'Nunito Sans';
		font-size: 15px;
	}
	.react-datepicker {
		border: ${(props) => (props.showDropShadow ? 'none' : `1px solid ${props.theme.colors.borderMedium}`)};
		box-shadow: ${(props) => (props.showDropShadow ? BoxShadow : 'none')};
		background: ${({ theme }) => theme.colors.surfaceElevated};
		color: ${({ theme }) => theme.colors.text};
	}

	.react-datepicker__day-name,
	.react-datepicker__current-month {
		color: ${({ theme }) => theme.colors.text};
	}

	.react-datepicker__day:hover {
		background: ${({ theme }) => theme.colors.surfaceSecondary};
	}
	.react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle, .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before {
		border-bottom-color: ${({ theme }) => theme.colors.surfaceElevated};
	}
	/* The popper flips above the field when there is no room below; the library paints
	   that triangle #aeaeae unless the top placement is themed too. */
	.react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle, .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle::before {
		border-top-color: ${({ theme }) => theme.colors.surfaceElevated};
	}
	.react-datepicker__day-names, .react-datepicker__week {
		margin-bottom: 15px;
	}
	/* The library paints every day #000 and has no --inside-month class, so the base
	   class carries the text token; --outside-month below dims the spill-over days. */
	.react-datepicker__day {
		color: ${({ theme }) => theme.colors.text};
	}
	.react-datepicker__day--outside-month {
		color: ${({ theme }) => theme.colors.textTertiary};
	}
	.react-datepicker__day--selected {
		background-color: #FF6600;
		color: white;
	}
	/* Stay blank while the calendar is unfocused so a mouse user sees no stray highlight
	   competing with the selected day. */
	.react-datepicker__day--keyboard-selected {
		background: none;
		color: ${({ theme }) => theme.colors.text};
	}
	/* Arrow keys move real DOM focus between day cells, so :focus tracks the keyboard
	   cursor. It has to be a box-shadow: global.scss strips outlines from div:focus with
	   !important, and day cells are divs. */
	/* The color matters: without it the selected day keeps its white text from
	   --selected (0,2,0) while this rule (0,3,0) replaces the orange background,
	   leaving white on light blue. */
	.react-datepicker__day:focus {
		outline: none;
		background: ${({ theme }) => (theme.isDark ? FOCUS_FILL_DARK : FOCUS_FILL)};
		color: ${({ theme }) => theme.colors.text};
		box-shadow: inset 0 0 0 2px ${({ theme }) => (theme.isDark ? FOCUS_BLUE : '#BFDBFE')};
	}
  
  label {
    margin-bottom: 0px;
  }
`;

const CalendarIconContainer = styled('div')`
  border-radius: 6px;
  border: none;
  // font-size: 12px;
  height: 40px;
  padding-left: 10px;
  font-weight: 400;
  box-shadow: ${(props) => (props.showDropShadow ? BoxShadow : 'none')};
  width: 100%;
  background: ${({ theme }) => theme.colors.surfaceInput};
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const IconContainer = styled('div')`
  background: ${(props) => props.iconBackgroundColor || 'transparent'};
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  height: 100%;
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

/* Id of the document.body-level portal node react-datepicker's `portalId` creates.
   Rendering the calendar there escapes the modal's transformed .ReactModal__Content
   (BaseModal's slide-in animation), which would otherwise trap the popover inside
   the modal box. */
const MOBILE_PORTAL_ID = 'tb-datepicker-portal';

/* On touch the calendar is rendered (via `portalId`) into a node appended to
   document.body -- OUTSIDE the scoped DateSelectorContainer -- so the TeamBuildr
   theme above can't reach it. Re-apply it, scoped under `#tb-datepicker-portal`
   so only this portal is affected (never desktop popovers or other pickers), and
   give the floated calendar a shadow + a z-index above the react-modal overlay.
   Injected via <Global> only when the mobile picker is active. */
const mobilePortalStyles = css`
  #${MOBILE_PORTAL_ID} .react-datepicker-popper {
    z-index: 2147483647;
  }
  #${MOBILE_PORTAL_ID} .react-datepicker {
    border: none;
    box-shadow: ${BoxShadow};
    font-family: 'Nunito Sans';
  }
  #${MOBILE_PORTAL_ID} .react-datepicker__header {
    background: white;
    border-bottom: none;
  }
  #${MOBILE_PORTAL_ID} .react-datepicker__month-container {
    padding: 15px;
    font-family: 'Nunito Sans';
    font-size: 15px;
  }
  #${MOBILE_PORTAL_ID} .react-datepicker__day-names,
  #${MOBILE_PORTAL_ID} .react-datepicker__week {
    margin-bottom: 15px;
  }
  #${MOBILE_PORTAL_ID} .react-datepicker__day--outside-month {
    color: #BDBDBD;
  }
  #${MOBILE_PORTAL_ID} .react-datepicker__day--inside-month {
    color: #444444;
  }
  #${MOBILE_PORTAL_ID} .react-datepicker__day--selected {
    background-color: #FF6600;
    color: white;
  }
  #${MOBILE_PORTAL_ID} .react-datepicker__day--keyboard-selected {
    background: none;
    color: #444444;
  }
`;

/* Touch trigger for the mobile picker. A <button> (not a text input) is what
   keeps the soft keyboard from appearing while still opening the calendar --
   react-datepicker won't open a `readOnly` input (onInputClick early-returns on
   readOnly, and isCalendarOpen requires !readOnly), so keyboard-suppression has
   to come from the element type, not readOnly. react-datepicker injects value,
   onClick, onFocus and onKeyDown; forwarding onFocus keeps the calendar-icon's
   setFocus() working. Mirrors the Workout Results Report's date picker. */
const MobileDateButton = React.forwardRef(({
  value, onClick, onFocus, onKeyDown, className,
}, ref) => (
  <button
    type='button'
    ref={ref}
    onClick={onClick}
    onFocus={onFocus}
    onKeyDown={onKeyDown}
    className={className}
    style={{
      width: '100%',
      height: '40px',
      border: 'none',
      background: 'transparent',
      textAlign: 'left',
      fontFamily: 'Nunito Sans',
      fontWeight: 400,
      fontSize: '14px',
      color: value ? '#444444' : '#9E9E9E',
      cursor: 'pointer',
      padding: 0,
    }}
  >
    {value || 'Select date'}
  </button>
));

MobileDateButton.displayName = 'MobileDateButton';

MobileDateButton.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  className: PropTypes.string,
};

MobileDateButton.defaultProps = {
  value: '',
  onClick: () => {},
  onFocus: () => {},
  onKeyDown: () => {},
  className: '',
};

/**
 * @param {string} activeDate a date string indicating the default state of the picker
 * @param {Function} setActiveDate function for setting a new date
 * @param {Object} additionalStyles optional styling object for customization
 * @param {Date|null} maxDate max selectable date (undefined = today, null = no limit)
 * @param {Date} minDate min selectable date (undefined = no limit)
 * @param {boolean} showDropShadow whether to show the drop shadow (defaults to true)
 * @param {boolean} showIcon whether to show the calendar icon and grey box (defaults to true)
 * @param {string} iconBackgroundColor background color for the icon container
 * (defaults to transparent)
 * @returns a DatePicker component fully styled in the TeamBuildr theme
 */
const TBDatePicker = ({
  setActiveDate,
  activeDate,
  disabled,
  additionalStyles,
  maxDate,
  minDate,
  showDropShadow,
  showIcon,
  iconBackgroundColor,
  mobileFriendly,
}) => {
  const datePickerRef = React.useRef(null);

  /* On a touch device, opt-in callers get a keyboard-free, on-screen calendar:
     a <button> customInput (so iOS/Android never raise the soft keyboard) plus
     `portalId`, which renders the calendar in a document.body-level node so it
     escapes the modal's transformed ancestor and any clipping while staying a
     popover near the field. Gated on capability, so a narrow desktop window --
     which is not touch -- keeps the normal typeable popover. */
  const isTouchDevice = useIsTouchDevice();
  const useMobilePicker = mobileFriendly && isTouchDevice;

  /* An invalid date is truthy, and react-datepicker's own guard only checks truthiness
     before handing the value to date-fns v2 format(), which throws RangeError on invalid
     input and takes down the whole app. Validate here so no consumer can trigger that --
     notably a cleared input, which some callers turn into the string 'Invalid date'. */
  const parsedDate = moment(activeDate);
  const selectedDate = activeDate && parsedDate.isValid() ? parsedDate.toDate() : null;

  /* react-datepicker closes the calendar on outside click, Escape or selecting a date --
     never on blur -- so tabbing out would leave it hanging open over the page. */
  const handleFocusOut = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      datePickerRef.current?.setOpen(false);
    }
  };

  /* Modality is tracked module-level rather than per-instance: switching date modes remounts
     this component, and a fresh instance would start out assuming a mouse and so miss the
     keypress that caused the remount. */
  React.useEffect(() => subscribeKeyboardNav(), []);

  /* Tabbing into the input opens the calendar but leaves focus on the input, so the user has
     to press ArrowDown before the arrow keys do anything. Put focus straight onto the
     pre-selected day instead. Mouse users keep focus in the input so they can still type a
     date -- which is also why this is gated on the keyboard flag. */
  const handleCalendarOpen = () => {
    if (!isKeyboardNav()) {
      return;
    }
    const focusableDay = datePickerRef.current?.calendar?.componentNode
      ?.querySelector('.react-datepicker__day[tabindex="0"]');
    focusableDay?.focus({ preventScroll: true });
  };

  return (
    <DateSelectorContainer
      additionalStyles={additionalStyles}
      /* handleFocusOut closes the calendar when focus leaves the container -- but
         with the mobile picker the calendar is portaled to document.body, OUTSIDE
         this container, so tapping a day blurs the trigger and would close the
         calendar before the day's click registers (selection silently lost). It's
         only needed to close on keyboard tab-out, which touch never does, so drop
         it on mobile; react-datepicker still closes on select and outside-click. */
      onBlur={useMobilePicker ? undefined : handleFocusOut}
      showDropShadow={showDropShadow}
    >
      {/* Renders nothing in the DOM (injects into <head>); themes the portaled
          calendar on touch. Kept inside the container so the surrounding JSX
          indentation is unchanged. */}
      {useMobilePicker && <Global styles={mobilePortalStyles} />}
      <CalendarIconContainer showDropShadow={showDropShadow}>
        <DatePicker
          ref={datePickerRef}
          id='workouts-date-picker'
          name='workouts-date-picker'
          className='date-picker'
          selected={selectedDate}
          customInput={useMobilePicker ? <MobileDateButton /> : undefined}
          portalId={useMobilePicker ? MOBILE_PORTAL_ID : undefined}
          popperPlacement='top-start'
          /* Overriding preventOverflow replaces react-datepicker's default object, which ships
             escapeWithReference: true and boundariesElement: 'viewport'. That restores
             popper.js's own defaults -- escapeWithReference: false, so the clamp that keeps
             the calendar inside its scroll parent is actually applied. This matches the eight
             other react-datepicker call sites in this repo, which all override it the same
             way; TBDatePicker was the outlier. */
          popperModifiers={{
            preventOverflow: {
              enabled: true,
            },
          }}
          disabled={disabled}
          /* Its tab-loop sentinels are div[tabindex=0] whose onFocus bounces focus back
             into the popper, trapping Tab inside the open calendar. */
          enableTabLoop={false}
          onCalendarOpen={handleCalendarOpen}
          onChange={(date) => {
            setActiveDate(date);
          }}
          maxDate={maxDate === null ? undefined : (maxDate || moment().toDate())}
          minDate={minDate}
          renderCustomHeader={({
            decreaseMonth,
            increaseMonth,
            date,
          }) => (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '20px',
            }}
            >
              <button
                aria-label='Previous month'
                type='button'
                onClick={decreaseMonth}
                onMouseDown={preventMouseDownDefault}
                style={{
                  border: 'none',
                  marginLeft: '10px',
                  background: 'rgba(95, 96, 100, 0.05)',
                  borderRadius: '5px',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='left-arrow'
                  color='#FF6600'
                />
              </button>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                fontFamily: 'Nunito Sans',
                fontWeight: '700',
                fontSize: '16px',
              }}
              >
                {date?.toLocaleString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <button
                aria-label='Next month'
                type='button'
                onClick={increaseMonth}
                onMouseDown={preventMouseDownDefault}
                style={{
                  border: 'none',
                  marginRight: '10px',
                  background: 'rgba(95, 96, 100, 0.05)',
                  borderRadius: '5px',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IcomoonReact
                  iconSet={iconSet}
                  size={15}
                  icon='right-arrow'
                  color='#FF6600'
                />
              </button>
            </div>
          )}
        />
        {showIcon && (
          <IconContainer
            iconBackgroundColor={iconBackgroundColor}
            onClick={() => datePickerRef.current?.setFocus()}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon='calendar'
              color='#9E9E9E'
            />
          </IconContainer>
        )}
      </CalendarIconContainer>
    </DateSelectorContainer>
  );
};

TBDatePicker.propTypes = {
  activeDate: PropTypes.string.isRequired,
  setActiveDate: PropTypes.func.isRequired,
  additionalStyles: PropTypes.objectOf(PropTypes.string),
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  showDropShadow: PropTypes.bool,
  showIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  iconBackgroundColor: PropTypes.string,
  mobileFriendly: PropTypes.bool,
};

TBDatePicker.defaultProps = {
  additionalStyles: {},
  maxDate: undefined,
  minDate: undefined,
  showDropShadow: true,
  showIcon: true,
  disabled: false,
  iconBackgroundColor: 'transparent',
  mobileFriendly: false,
};

export default TBDatePicker;
